import { Subject } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnCamera, SnStreamSources } from '@lib/api/entities';
import { SnAspectRatio } from '@lib/common/enums';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';
import { SnVideoPlayerComponent } from '@lib/player/video-player';
import { TimeoutDialogComponent } from '@lib/ui/dialog';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';

import { SnVerkadaService } from '../../services';
import { SnVerkadaStreamResponse } from '../../types';
import {
  VerkadaDatetimePickerModalComponent,
  DateTimePickerResult,
} from '../verkada-datetime-picker-modal/verkada-datetime-picker-modal.component';

/** Stop loading streams after this timeout. */
const DEFAULT_LOAD_TIMEOUT_MS = 10000;
/** THe default time to seek to when the video starts playing in historical mode (eg: #0 minutes). */
const DEFAULT_SEEK_TO_TIME_SECONDS = 1800;
/** Threshold in minutes to show stream expiry warning. */
const STREAM_EXPIRY_WARNING_THRESHOLD_MINUTES = 1;

@Component({
  selector: 'lib-verkada-player',
  templateUrl: './verkada-player.component.html',
  styleUrls: ['./verkada-player.component.scss'],
})
export class VerkadaPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() camera: SnCamera | undefined;
  @Input() caption = 'Verkada Camera Player';
  @Input() autoplay = true;
  @Input() isFullscreen = false;

  @Output() fullscreenChange = new EventEmitter<boolean>();
  @Output() playerStart = new EventEmitter<void>();
  @Output() playerStop = new EventEmitter<void>();

  @ViewChild(SnVideoPlayerComponent) videoPlayer!: SnVideoPlayerComponent;

  private readonly _destroy$ = new Subject<void>();

  public ASPECT_RATIO = SnAspectRatio;
  public isHistoricalMode = false;
  public historicalStreamData: SnVerkadaStreamResponse | null = null;
  public historicalStartTime: Date | null = null;
  public historicalEndTime: Date | null = null;
  public isLoadingHistorical = false;
  public isSeekingToInitialPosition = false; // Track if we're seeking to the initial 30-minute position

  private _cachedHistoricalStreamUrl: SnStreamSources | null = null;
  private _cachedLiveStreamUrl: SnStreamSources | null = null;
  private _currentPlayerState: SnPlayerState = SnPlayerState.Pause;
  private _fastForwardLiveCheckInterval?: NodeJS.Timeout; // Interval to check if we hit live edge.
  private _jwtExpiryTime: number | null = null;
  private _jwtExpiryTimer?: NodeJS.Timeout;
  private _jwtExpiryWarningShown = false; // Flag to track if the expiry warning has been shown
  private _historicalLoadTimeout?: NodeJS.Timeout;
  private _historicalModeDestroy$ = new Subject<void>();
  private _isFastForwardingInLive = false; // Track if we're fast forwarding in live mode
  private _lastHistoricalUrl: string | null = null;
  private _lastLiveStreamUrl: SnStreamSources | null = null;
  private _lastLiveHlsUrl: string | undefined = undefined; // Track the actual HLS URL string.

  get currentStreamUrl(): SnStreamSources {
    if (this.isHistoricalMode && this.historicalStreamData?.url) {
      if (this._lastHistoricalUrl !== this.historicalStreamData.url) {
        this._lastHistoricalUrl = this.historicalStreamData.url;
        this._cachedHistoricalStreamUrl = { hls: this.historicalStreamData.url } as SnStreamSources;
      }
      return (
        this._cachedHistoricalStreamUrl ||
        ({ hls: this.historicalStreamData.url } as SnStreamSources)
      );
    }
    const currentHlsUrl = this.camera?.liveVideoUrls?.main?.cloud?.hls || this.camera?.unmanagedUrl;
    const liveStreamUrl = this.camera?.liveVideoUrls?.main?.cloud || ({} as SnStreamSources);
    /** If the HLS URL changed, invalidate cache and update the video player. */
    if (this._lastLiveHlsUrl !== currentHlsUrl) {
      this._lastLiveHlsUrl = currentHlsUrl;
      this._lastLiveStreamUrl = null;
      this._cachedLiveStreamUrl = null;
      if (!this.isHistoricalMode && currentHlsUrl && this.videoPlayer) {
        setTimeout(() => {
          if (!this.isHistoricalMode) {
            this.videoPlayer.src = this.currentStreamUrl;
            if (this.autoplay) {
              this.videoPlayer.play();
            }
          }
        }, 0);
      }
    }
    if (this._lastLiveStreamUrl !== liveStreamUrl) {
      this._lastLiveStreamUrl = liveStreamUrl;
      this._cachedLiveStreamUrl = liveStreamUrl;
    }
    return this._cachedLiveStreamUrl || ({} as SnStreamSources);
  }

  get playbackStatusText(): string {
    if (this.videoPlayer?.reference?.playbackRate > 1) {
      return `Fast Forwarding x${this.videoPlayer?.reference?.playbackRate}`;
    } else if (this.videoPlayer?.reference?.rewindSpeedFactor > 0) {
      return `Rewinding x${this.videoPlayer?.reference?.rewindSpeedFactor}`;
    } else if (this._currentPlayerState === SnPlayerState.Pause) {
      return 'Paused';
    } else return '';
  }

  constructor(
    private readonly dialog: MatDialog,
    private readonly datadogService: FsDataDogRumService,
    private readonly verkadaService: SnVerkadaService,
  ) {}

  ngOnInit(): void {
    if (!this.caption && this.camera?.name) this.caption = this.camera.name;
    this._updateJwtExpiryTime(this.camera?.streamTokenExpiryTime);
    this.datadogService.addAction('verkada_live_stream_opened', {
      cameraId: this.camera?.id || 'unknown',
    });
  }

  ngAfterViewInit(): void {
    this.videoPlayer.stateChange
      .pipe(takeUntil(this._destroy$))
      .subscribe((state: SnPlayerState) => {
        this._currentPlayerState = state;
        if (state === SnPlayerState.Playing) {
          this.onPlayerStart();
          if (this.isLoadingHistorical && this.isHistoricalMode) {
            this.isLoadingHistorical = false;
            if (this._historicalLoadTimeout) {
              clearTimeout(this._historicalLoadTimeout);
              this._historicalLoadTimeout = undefined;
            }
          }
        } else if (state === SnPlayerState.Pause) {
          this.onPlayerStop();
        }
      });

    this.setPlayerLiveStatus(true); // Player will always start in live mode!
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._historicalModeDestroy$.next();
    this._historicalModeDestroy$.complete();
    this._stopLiveEdgeMonitoring();
    this._isFastForwardingInLive = false;
    this._stopJwtExpiryTimer();
  }

  private onPlayerStart(): void {
    this.playerStart.emit();
  }

  private onPlayerStop(): void {
    this.setPlayerLiveStatus(false); // Once the player stops, the player is no longer in live mode.
    this.playerStop.emit();
  }

  /** Sets the LIVE status of the video player. */
  public setPlayerLiveStatus(isLive: boolean): void {
    if (this.videoPlayer) {
      this.videoPlayer.live = isLive;
    }
  }

  /** Toggles the calendar display and emits the toggleCalendar event. */
  public onToggleCalendar(): void {
    const dialogRef = this.dialog.open(VerkadaDatetimePickerModalComponent, {
      width: '450px',
      data: {
        title: 'Select Date and Time',
        initialDate: new Date(),
      },
    });
    dialogRef.afterClosed().subscribe((result: DateTimePickerResult) => {
      if (result) {
        this.onDateTimeSelected(result.selectedDateTime);
      }
    });
  }

  /** Event listener for when a date and time is selected from the modal */
  private onDateTimeSelected(selectedDateTime: Date): void {
    if (!this.camera?.id) {
      return;
    }
    const nowEpoch = Math.floor(Date.now() / 1000);
    const selectedEpoch = Math.floor(selectedDateTime.getTime() / 1000);
    const diffFromNowSeconds = nowEpoch - selectedEpoch;
    let startEpoch: number;
    let endEpoch: number;
    if (diffFromNowSeconds > DEFAULT_SEEK_TO_TIME_SECONDS) {
      startEpoch = selectedEpoch - DEFAULT_SEEK_TO_TIME_SECONDS;
      endEpoch = selectedEpoch + DEFAULT_SEEK_TO_TIME_SECONDS;
    } else {
      endEpoch = nowEpoch;
      startEpoch = nowEpoch - 2 * DEFAULT_SEEK_TO_TIME_SECONDS;
    }
    this.isLoadingHistorical = true;
    this.verkadaService
      .getStream({
        cameraId: this.camera.id,
        startTime: startEpoch,
        endTime: endEpoch,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (streamResponse: SnVerkadaStreamResponse) => {
          if (streamResponse?.url) {
            this.switchToHistoricalMode(
              streamResponse,
              new Date(startEpoch * 1000),
              new Date(endEpoch * 1000),
            );
            this._updateJwtExpiryTime(streamResponse.expiresAt);
          }
          this.isLoadingHistorical = false;
        },
        error: () => {
          this.isLoadingHistorical = false;
        },
      });
  }

  private switchToHistoricalMode(
    streamData: SnVerkadaStreamResponse,
    startTime: Date,
    endTime: Date,
    seekToTime: number = DEFAULT_SEEK_TO_TIME_SECONDS,
  ): void {
    if (!this.videoPlayer) {
      return;
    }
    this.datadogService.addAction('verkada_switched_to_historical_stream', {
      cameraId: this.camera?.id || 'unknown',
    });
    this.historicalStartTime = startTime;
    this.historicalEndTime = endTime;
    this.historicalStreamData = streamData;
    this.isHistoricalMode = true;
    this.isSeekingToInitialPosition = true; // Start seeking to initial position
    this._resetStreamCaches();
    this.videoPlayer.src = this.currentStreamUrl;
    this.setPlayerLiveStatus(false);
    this.videoPlayer.play();
    this.videoPlayer.stateChange
      .pipe(
        takeUntil(this._historicalModeDestroy$),
        filter((state) => state === SnPlayerState.Playing),
      )
      .subscribe(() => {
        if (this.isSeekingToInitialPosition) {
          this._attemptSeekWithRetry(seekToTime);
        }
      });
    this._historicalLoadTimeout = setTimeout(() => {
      if (this.isLoadingHistorical) {
        this.isLoadingHistorical = false;
        this.switchToLiveMode();
      }
    }, DEFAULT_LOAD_TIMEOUT_MS);
  }

  public switchToLiveMode(): void {
    if (!this.videoPlayer) {
      return;
    }
    this.datadogService.addAction('verkada_switched_to_live_stream', {
      cameraId: this.camera?.id || 'unknown',
    });
    this._historicalModeDestroy$.next();
    this.isHistoricalMode = false;
    this.historicalStreamData = null;
    this.historicalStartTime = null;
    this.historicalEndTime = null;
    this.isSeekingToInitialPosition = false; // Reset seeking flag when switching to live mode
    this._resetStreamCaches();
    this.videoPlayer.src = this.currentStreamUrl;
    this._updateJwtExpiryTime(this.camera?.streamTokenExpiryTime);
    this.setPlayerLiveStatus(true);
    if (this.autoplay) this.videoPlayer.play();
    if (this._historicalLoadTimeout) {
      clearTimeout(this._historicalLoadTimeout);
      this._historicalLoadTimeout = undefined;
    }
  }

  public onFullscreenChange(isFullscreen: boolean): void {
    this.fullscreenChange.emit(isFullscreen);
  }

  public jumpToBeginning(): void {
    if (!this.videoPlayer) return;
    this.videoPlayer.changeCurrentTime(0);
    this.videoPlayer.play();
    if (!this.isHistoricalMode) {
      this.setPlayerLiveStatus(false);
    }
  }

  public back(seconds: number): void {
    if (!this.videoPlayer) return;
    this.videoPlayer.back(seconds);
    if (!this.isHistoricalMode) {
      this.setPlayerLiveStatus(false);
    }
  }

  public forward(seconds: number): void {
    if (!this.videoPlayer) return;
    this.videoPlayer.forward(seconds);
  }

  public jumpToEnd(): void {
    if (!this.videoPlayer) return;
    if (this.isHistoricalMode) {
      this.videoPlayer.totalTime$.pipe(take(1)).subscribe((totalTime) => {
        if (totalTime > 0) {
          this.videoPlayer.changeCurrentTime(totalTime - 1); // Seek 1 second before the end, so the frames update.
          setTimeout(() => this.videoPlayer.pause(), 100); // Pause after seeking to the end.
        }
      });
    } else {
      this.videoPlayer.totalTime$.pipe(take(1)).subscribe((totalTime) => {
        if (totalTime > 0) {
          this.videoPlayer.changeCurrentTime(totalTime);
          this.videoPlayer.play();
          this.setPlayerLiveStatus(true);
        }
      });
    }
  }

  public rewind(): void {
    if (!this.videoPlayer?.reference) return;
    this.videoPlayer.reference.rewind();
  }

  public fastForward(): void {
    if (!this.videoPlayer?.reference) return;
    if (!this.isHistoricalMode) {
      this._isFastForwardingInLive = true;
      this._startLiveEdgeMonitoring();
    }
    this.videoPlayer.reference?.fastForward();
  }

  public screenshot(): void {
    const video = this.videoPlayer.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const dialogRef = this.dialog.open(SnShareImageDialogComponent, {
      data: {
        url: canvas.toDataURL(),
        playerRef: this.videoPlayer,
        hideSnapAi: true, // Hide Snap AI. Not supported by Verkada player.
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public liveButtonClick(): void {
    if (this.isHistoricalMode) this.switchToLiveMode();
    else this.jumpToEnd();
  }

  public handleStreamClose(): void {
    this.datadogService.addAction('verkada_stream_end_clicked', {
      cameraId: this.camera?.id || 'unknown',
    });
    if (this.camera?.cameraMeta?.cameraRemoved$) {
      this.camera.cameraMeta.cameraRemoved$.next(this.camera);
    }
  }

  public handleStreamContinue(): void {
    this.datadogService.addAction('verkada_stream_continue_clicked', {
      cameraId: this.camera?.id || 'unknown',
    });
    const startTime = this.historicalStartTime;
    const endTime = this.historicalEndTime;
    if (!this.camera?.id) return;
    if (this.isHistoricalMode) {
      const currentTime = this.videoPlayer?.currentTime || 0; // Store the current time before switching modes.
      if (startTime && endTime) {
        this.isLoadingHistorical = true;
        this.verkadaService
          .getStream({
            cameraId: this.camera.id,
            startTime: Math.floor(startTime.getTime() / 1000),
            endTime: Math.floor(endTime.getTime() / 1000),
          })
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: (streamResponse: SnVerkadaStreamResponse) => {
              if (streamResponse?.url) {
                this.switchToHistoricalMode(streamResponse, startTime, endTime, currentTime);
                this._updateJwtExpiryTime(streamResponse.expiresAt);
              }
              this.isLoadingHistorical = false;
            },
            error: () => (this.isLoadingHistorical = false),
          });
      }
    } else {
      this.verkadaService
        .getStream({ cameraId: this.camera.id })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (streamResponse: SnVerkadaStreamResponse) => {
            if (streamResponse?.url && this.camera) {
              this.camera.unmanagedUrl = streamResponse.url;
              this.camera.streamTokenExpiryTime = streamResponse.expiresAt;
              if (this.camera.liveVideoUrls?.main?.cloud)
                this.camera.liveVideoUrls.main.cloud.hls = streamResponse.url;
              this._resetStreamCaches();
              this.videoPlayer.src = this.currentStreamUrl;
              this._updateJwtExpiryTime(streamResponse.expiresAt);
              this.switchToLiveMode();
              if (this.autoplay) this.videoPlayer.play();
            }
          },
          error: (err) => console.error(`Failed to reload live stream: ${err}`),
        });
    }
  }

  /** This method seeks to the 30-minute mark in the historical timeline. */
  private _attemptSeekWithRetry(seekToTime: number): void {
    const retryInterval = 100;
    const startTime = Date.now();
    const attemptSeek = (): void => {
      if (!this.videoPlayer || !this.isHistoricalMode) {
        this.isSeekingToInitialPosition = false;
        return;
      }
      try {
        const currentTime = this.videoPlayer.currentTime || 0;
        this.videoPlayer.totalTime$.pipe(take(1)).subscribe((totalTime) => {
          if (totalTime > 0 && !isNaN(totalTime) && currentTime >= 0 && seekToTime <= totalTime) {
            this.videoPlayer.changeCurrentTime(seekToTime);
            this.isSeekingToInitialPosition = false;
            return;
          }
          const elapsed = Date.now() - startTime;
          if (elapsed >= DEFAULT_LOAD_TIMEOUT_MS) {
            this.isSeekingToInitialPosition = false;
            return;
          }
          setTimeout(attemptSeek, retryInterval);
        });
      } catch (error) {
        const elapsed = Date.now() - startTime;
        if (elapsed < DEFAULT_LOAD_TIMEOUT_MS) {
          setTimeout(attemptSeek, retryInterval);
        } else {
          this.isSeekingToInitialPosition = false;
        }
      }
    };
    setTimeout(attemptSeek, retryInterval);
  }

  private _resetStreamCaches(): void {
    this._cachedHistoricalStreamUrl = null;
    this._cachedLiveStreamUrl = null;
    this._lastHistoricalUrl = null;
    this._lastLiveStreamUrl = null;
    this._lastLiveHlsUrl = undefined;
  }

  /**
   * Starts monitoring for when fast forwarding hits the live edge.
   * When detected, automatically stops fast forwarding and resumes normal playback.
   */
  private _startLiveEdgeMonitoring(): void {
    this._stopLiveEdgeMonitoring();
    this._fastForwardLiveCheckInterval = setInterval(() => {
      if (!this._isFastForwardingInLive || this.isHistoricalMode || !this.videoPlayer) {
        this._stopLiveEdgeMonitoring();
        return;
      }
      const isCurrentlyFastForwarding = this.videoPlayer.reference.playbackRate > 1;
      if (!isCurrentlyFastForwarding) {
        this._isFastForwardingInLive = false;
        this._stopLiveEdgeMonitoring();
        return;
      }
      this.videoPlayer.totalTime$.pipe(take(1)).subscribe((totalTime) => {
        const currentTime = this.videoPlayer.currentTime;
        const timeFromEnd = totalTime - currentTime;
        if (timeFromEnd <= 1) {
          this._stopFastForwardingAndResumePlay();
        }
      });
    }, 500);
  }

  private _stopLiveEdgeMonitoring(): void {
    if (this._fastForwardLiveCheckInterval) {
      clearInterval(this._fastForwardLiveCheckInterval);
      this._fastForwardLiveCheckInterval = undefined;
    }
  }

  private _stopFastForwardingAndResumePlay(): void {
    if (!this.videoPlayer) return;
    this.videoPlayer.reference.playbackRate = 1;
    this.videoPlayer.reference.isFastForwarding = false;
    this.setPlayerLiveStatus(true);
    this.videoPlayer.play();
    this._isFastForwardingInLive = false;
    this._stopLiveEdgeMonitoring();
  }

  private _updateJwtExpiryTime(streamTokenExpiryTime: string | undefined): void {
    if (typeof streamTokenExpiryTime === 'string') {
      const date = new Date(streamTokenExpiryTime);
      if (isNaN(date.getTime())) return;
      this._jwtExpiryTime = Math.floor(date.getTime() / 1000);
      this._jwtExpiryWarningShown = false; // Reset the warning shown flag when updating expiry time.
      this._stopJwtExpiryTimer();
      this._startJwtExpiryTimer();
    }
  }

  private _startJwtExpiryTimer(): void {
    this._jwtExpiryTimer = setInterval(() => this._checkJwtExpiryText(), 1000);
  }

  private _stopJwtExpiryTimer(): void {
    if (this._jwtExpiryTimer) {
      clearInterval(this._jwtExpiryTimer);
      this._jwtExpiryTimer = undefined;
    }
  }

  private _checkJwtExpiryText(): void {
    if (!this._jwtExpiryTime || isNaN(this._jwtExpiryTime)) return;
    const timeRemaining = this._jwtExpiryTime - Math.floor(Date.now() / 1000);
    if (!isNaN(timeRemaining)) {
      if (timeRemaining <= 0) {
        /** If the JWT has expired, close the stream. */
        this.datadogService.addAction('verkada_stream_timeout_expired', {
          cameraId: this.camera?.id || 'unknown',
        });
        this.handleStreamClose();
      } else if (
        timeRemaining <= STREAM_EXPIRY_WARNING_THRESHOLD_MINUTES * 60 &&
        !this._jwtExpiryWarningShown
      ) {
        /** If the time remaining is less than or equal to the threshold, show a warning dialog. */
        this.datadogService.addAction('verkada_timeout_dialog_shown', {
          cameraId: this.camera?.id || 'unknown',
        });
        const dialogRef = this.dialog.open(TimeoutDialogComponent, {
          width: '450px',
          disableClose: true,
          data: {
            title: `Verkada session is about to expire`,
            message: `Your video session is about to end. Would you like to keep watching?`,
            timeout: timeRemaining * 1000, // Convert seconds to milliseconds
          },
        });
        dialogRef
          .afterClosed()
          .pipe(takeUntil(this._destroy$))
          .subscribe((result) => {
            if (result === false) {
              this.handleStreamClose();
            } else if (result === true) {
              this.handleStreamContinue();
            }
          });
        this._jwtExpiryWarningShown = true;
      }
    }
  }
}
